import React, { useCallback, useEffect, useMemo } from 'react'
import { FormikProps, useFormikContext } from 'formik'
import {
  CommonDimensionField,
  Product,
  ProductPlan,
} from '../../../../../modules/productsListing/reducer'
import { useStyles } from './PrivateOfferPlans.styles'
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import {
  Box,
  LabraToolTip,
  LabraToolTipV2,
  TextInput,
} from '@labrav/react-components'
import { InfoOutlined } from '@mui/icons-material'
import clsx from 'clsx'
import {
  GcpPostPaymentTypes,
  PaymentType,
  PaymentTypeGCP,
  PlanTypeGCP,
  PrivateOffer,
} from '../../../../../modules/privateOffer/reducer'
import { get, isEmpty, isNil } from 'lodash'
import { getValidValueBetweenMinMax, isNonEmptyNumber } from '@labrav/common'
import { InstallmentsPrivateOffer } from '../InstallmentsPrivateOffer/InstallmentsPrivateOffer'
import { PlanFeatures } from './PlanFeatures/PlanFeatures'
import { MetricDiscount } from '../MetricDiscount/MetricDiscount'
import { getDimensionFromProduct } from '../../../../../modules/privateOffer/privateOfferCreation/utils/dimensionHelper'
import {
  flatFeeText,
  getTcvEstimateDetail,
  getValidDurationDetail,
  getdurationDetailFlatFee,
} from '@labrav/marketplace'
import { useCommonStyles } from '../../../../../common/styles/common.styles'

export type PrivateOfferAddEditProps = {
  product: Product
}
export const PrivateOfferPlans: React.FC<PrivateOfferAddEditProps> = ({
  product,
}) => {
  const { classes: commonClasses } = useCommonStyles()
  const formikContext = useFormikContext() as FormikProps<PrivateOffer>
  const {
    values,
    errors: error,
    touched,
    handleBlur,
    setFieldValue,
    handleChange,
    setTouched,
  } = formikContext
  const { classes } = useStyles()
  const isUsage = values?.plan?.planType === PlanTypeGCP.USAGE
  const paymentTypeOptions = useMemo(() => {
    return Object.entries(PaymentTypeGCP).map(([key, value]) => ({
      label: value,
      value: key,
      disabled: key === PaymentType.CUSTOM,
    }))
  }, [isUsage])

  const handleInputChangeSelect = useCallback(
    async (event: unknown) => {
      const { value, name } = (event as React.ChangeEvent<HTMLInputElement>)
        .target
      if (name === 'plan') {
        const productPlan = product?.plans?.find(plan => plan.planId === value)
        if (productPlan) {
          const { displayName, description, planId, planType } = productPlan
          const offerDimensions = getDimensionFromProduct(productPlan)

          await setFieldValue('plan', {
            displayName,
            description,
            planId,
            planType,
            ...(productPlan.planType === 'USAGE'
              ? {
                  paymentType: '',
                  metricPricing: offerDimensions,
                }
              : { paymentType: '' }),
          })
          await setFieldValue('autoRenew', false)
          await setFieldValue('renewalCount', '')
          await setTouched({
            ...touched,
            plan: undefined,
            autoRenew: undefined,
            renewalCount: undefined,
          })
        }
      } else if (name === 'plan.paymentType') {
        const { displayName, description, planId, planType } = values.plan || {}
        const productPlan = product?.plans?.find(
          plan => plan.planId === values.plan?.planId
        )

        const offerDimensions = getDimensionFromProduct(productPlan)

        await setFieldValue('plan', {
          displayName,
          description,
          planId,
          planType,
          paymentType: value,
          ...(productPlan?.planType === PlanTypeGCP.FLAT_FEE_USAGE &&
          GcpPostPaymentTypes.includes(value as unknown as PaymentType)
            ? {
                metricPricing: offerDimensions,
              }
            : {}),
        })
        await setTouched({
          ...touched,
          plan: undefined,
        })
      }
    },
    [product?.plans, values?.plan, values?.plan?.paymentType, touched]
  )

  const supportingText = (paymentType?: PaymentType) => {
    if (paymentType === PaymentType.MONTHLY) return '60'
    else if (paymentType === PaymentType.QUARTERLY) return '20'
    else if (paymentType === PaymentType.ANNUALLY) return '5'
    else return '60'
  }
  const productPlans = product?.plans?.map(plan => ({
    label: plan?.displayName,
    value: plan?.planId,
  }))

  const shouldShowMonthlyFlatFee =
    (values.plan?.planType === PlanTypeGCP.FLAT_FEE ||
      values.plan?.planType === PlanTypeGCP.FLAT_FEE_USAGE) &&
    values.plan?.paymentType !== PaymentType.CUSTOM

  const shouldShowDiscount =
    values.plan?.planType === PlanTypeGCP.USAGE ||
    values.plan?.planType === PlanTypeGCP.FLAT_FEE_USAGE

  const shouldShowInstallments =
    values.plan?.paymentType === PaymentType.CUSTOM &&
    !!values.plan.serviceLength
  const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(
      'plan.discount',
      getValidValueBetweenMinMax(e.target.value as string, 1, 100)
    )
  }
  const handleChangeNumberValue = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = event.target.value
    const value = isEmpty(inputValue)
      ? ''
      : parseInt(inputValue as string) < 0
        ? 0
        : inputValue
    setFieldValue(event.target.name, value)
  }

  return (
    <Box heading={'Plans'} subHeading={''}>
      <div
        className={clsx(
          classes.subHeadingContainer,
          classes.companyDetailSubHead
        )}
      >
        <Typography
          className={classes.subHeading}
          data-testid="details-subheading"
        >
          Plan
        </Typography>
        <LabraToolTip
          tooltipPlacement={'right'}
          label={'Add the plan that you want to offer the buyer.'}
        >
          <InfoOutlined className={classes.infoIcon} />
        </LabraToolTip>
      </div>
      <div
        className={classes.planSectionRowContainer}
        data-testid={'plan-type'}
      >
        <Grid container spacing={4} width={'100%'}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 4,
              lg: 4,
              xl: 4,
            }}
          >
            <FormControl className={classes.formWidth}>
              <InputLabel
                id="product-plan-label"
                className={classes.inputLabel}
                classes={{ shrink: classes.shrink }}
              >
                Select plan
              </InputLabel>
              <Select
                labelId="product-plan"
                value={values?.plan?.planId}
                onChange={handleInputChangeSelect}
                label="Select plan"
                inputProps={{ 'data-testid': 'input' }}
                variant="standard"
                className={classes.selectField}
                IconComponent={props => (
                  <KeyboardArrowDownIcon
                    {...props}
                    fontSize="small"
                    data-testid="arrow-button"
                  />
                )}
                classes={{ icon: classes.icon }}
                name={'plan'}
                data-testid="plan-name"
              >
                {(productPlans || []).map(plan => (
                  <MenuItem
                    data-testid="menu-item"
                    value={plan.value}
                    className={classes.menuRoot}
                    key={plan.value}
                  >
                    {plan.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {values?.plan?.description && (
            <Grid
              data-testid="description-plan-type"
              size={{
                xs: 12,
                sm: 6,
                md: 5,
                lg: 5,
                xl: 5,
              }}
            >
              <div className={classes.descriptionTitle}>Description</div>
              <p className={classes.description}>{values?.plan?.description}</p>
            </Grid>
          )}
        </Grid>
      </div>
      {values.plan?.planId && !isUsage && (
        <>
          <div
            className={clsx(
              classes.subHeadingContainer,
              classes.companyDetailSubHead,
              classes.marginTop
            )}
            data-testid="payment-schedule"
          >
            <Typography
              className={classes.subHeading}
              data-testid="subheading-payment-schedule"
            >
              Payment Schedule
            </Typography>
            <LabraToolTip
              tooltipPlacement={'right'}
              label={
                'The Payment Frequency dropdown allows you to select how often you wish to receive payments.'
              }
            >
              <InfoOutlined className={classes.infoIcon} />
            </LabraToolTip>
          </div>

          <div
            className={classes.planSectionRowContainer}
            data-testid={'payment-type'}
          >
            <FormControl className={classes.feildWidth}>
              <InputLabel
                id="product-plan-label"
                className={classes.inputLabel}
                classes={{ shrink: classes.shrink }}
              >
                Payment frequency
              </InputLabel>
              <Select
                labelId="product-plan"
                value={values?.plan?.paymentType}
                onChange={handleInputChangeSelect}
                label="Payment frequency"
                inputProps={{ 'data-testid': 'input' }}
                variant="standard"
                className={classes.selectField}
                IconComponent={props => (
                  <KeyboardArrowDownIcon
                    {...props}
                    fontSize="small"
                    data-testid="arrow-button"
                  />
                )}
                classes={{ icon: classes.icon }}
                name={'plan.paymentType'}
                data-testid="plan-payment-type"
              >
                {(paymentTypeOptions || []).map(mode =>
                  mode.disabled ? (
                    <LabraToolTipV2
                      tooltipPlacement="bottom"
                      description="Coming soon"
                    >
                      <MenuItem
                        data-testid="menu-item-payment-type"
                        value={mode.value}
                        className={classes.menuRoot}
                        key={mode.value}
                        disabled={mode.disabled}
                      >
                        {mode.label}
                      </MenuItem>
                    </LabraToolTipV2>
                  ) : (
                    <MenuItem
                      data-testid="menu-item-payment-type"
                      value={mode.value}
                      className={classes.menuRoot}
                      key={mode.value}
                      disabled={mode.disabled}
                    >
                      {mode.label}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </div>
        </>
      )}
      {(!isEmpty(values.plan?.paymentType) || isUsage) && (
        <>
          <div
            className={clsx(
              classes.subHeadingContainer,
              classes.companyDetailSubHead,
              classes.marginTop
            )}
            data-testid="contract-duration"
          >
            <Typography
              className={classes.subHeading}
              data-testid="details-subheading"
            >
              Contract duration
            </Typography>
            <LabraToolTip
              tooltipPlacement={'right'}
              label={
                'The duration for which the subscription/contract will run after acceptance by buyer.'
              }
            >
              <InfoOutlined className={classes.infoIcon} />
            </LabraToolTip>
          </div>
          <div
            className={classes.feildWidth}
            key={`contractDuration-${values.plan?.paymentType}-${values.plan?.planType}`}
          >
            <TextInput
              isRequired={false}
              keyName="plan.serviceLength"
              title={
                getValidDurationDetail(
                  values?.plan?.paymentType,
                  isUsage
                ) as string
              }
              updatedValue={values.plan?.serviceLength}
              value={values.plan?.serviceLength || ''}
              onChange={handleChangeNumberValue}
              type="number"
              onBlur={handleBlur}
              isError={
                !!(
                  get(touched, 'plan.serviceLength', false) &&
                  get(error, 'plan.serviceLength', false)
                )
              }
              errorText={
                (!!get(touched, 'plan.serviceLength', false) &&
                  get(error, 'plan.serviceLength', ' ')) ||
                ' '
              }
              isReadOnly={false}
              placeHolder=""
              variant="regularV2"
              keyToPrevent={['+', '-', 'e', '.']}
              supportingText={`Enter a number between 1-${supportingText(
                values?.plan?.paymentType
              )}`}
            />
          </div>
          <div
            className={classes.planSectionRowContainer}
            data-testid={'plan-type'}
          >
            <Grid container spacing={4}>
              {shouldShowMonthlyFlatFee ? (
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 6,
                    lg: 6,
                    xl: 6,
                  }}
                >
                  <div
                    className={clsx(
                      classes.subHeadingContainer,
                      classes.companyDetailSubHead,
                      classes.marginTop
                    )}
                    data-testid="monthly-flat-fee"
                  >
                    <Typography
                      className={classes.subHeading}
                      data-testid="details-subheading"
                    >
                      Flat Fee
                    </Typography>
                    <LabraToolTip
                      tooltipPlacement={'right'}
                      label={
                        'Enter subscription amount you intend to receive from buyer for using your services.'
                      }
                    >
                      <InfoOutlined className={classes.infoIcon} />
                    </LabraToolTip>
                  </div>
                  <div
                    className={classes.feildWidth}
                    key={`flatFeePrice-${values.plan?.paymentType}-${values.plan?.planType}`}
                  >
                    <TextInput
                      isRequired={false}
                      keyName="plan.flatFeePrice"
                      title={
                        getdurationDetailFlatFee(
                          values?.plan?.paymentType,
                          isUsage
                        ) as string
                      }
                      value={values.plan?.flatFeePrice as number}
                      updatedValue={values.plan?.flatFeePrice as number}
                      onChange={handleChangeNumberValue}
                      type="number"
                      onBlur={handleBlur}
                      isError={
                        !!(
                          get(touched, 'plan.flatFeePrice', false) &&
                          get(error, 'plan.flatFeePrice', false)
                        )
                      }
                      errorText={
                        (!!get(touched, 'plan.flatFeePrice', false) &&
                          get(error, 'plan.flatFeePrice', ' ')) ||
                        ' '
                      }
                      isReadOnly={false}
                      placeHolder={`Enter ${flatFeeText(
                        values?.plan?.paymentType
                      )} flat fee`}
                      variant="regularV2"
                      keyToPrevent={['+', '-', 'e']}
                      startAdornment={'$'}
                    />
                  </div>
                </Grid>
              ) : (
                <></>
              )}
              {shouldShowInstallments && (
                <Grid
                  size={{
                    xs: 12,
                    sm: 12,
                    md: 12,
                    lg: 12,
                  }}
                >
                  <InstallmentsPrivateOffer
                    formik={formikContext}
                    product={product}
                  />
                </Grid>
              )}
              {shouldShowDiscount ? (
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 6,
                    lg: 6,
                    xl: 6,
                  }}
                >
                  <div
                    className={clsx(
                      classes.subHeadingContainer,
                      classes.companyDetailSubHead,
                      classes.marginTop
                    )}
                    data-testid="discount-on-usage"
                  >
                    <Typography
                      className={classes.subHeading}
                      data-testid="details-subheading"
                    >
                      Discount on Usage (%)
                    </Typography>
                    <LabraToolTip tooltipPlacement={'right'} label={'-'}>
                      <InfoOutlined className={classes.infoIcon} />
                    </LabraToolTip>
                  </div>
                  <div
                    className={classes.feildWidth}
                    key={`discount-${values.plan?.paymentType}-${values.plan?.planType}`}
                  >
                    <TextInput
                      isRequired={false}
                      keyName="plan.discount"
                      title="Percentage of discount on usage (%)"
                      value={values.plan?.discount as number}
                      updatedValue={values.plan?.discount as number}
                      onChange={handleDiscountChange}
                      type="number"
                      onBlur={handleBlur}
                      isError={
                        !!(
                          get(touched, 'plan.discount', false) &&
                          get(error, 'plan.discount', false)
                        )
                      }
                      errorText={
                        (!!get(touched, 'plan.discount', false) &&
                          get(error, 'plan.discount', ' ')) ||
                        ' '
                      }
                      isReadOnly={!!values.plan?.individualDiscount}
                      placeHolder="Percentage of discount on usage (%)"
                      variant="regularV2"
                      keyToPrevent={['+', '-', 'e']}
                      minNumber={1}
                      maxNumber={100}
                    />
                  </div>
                </Grid>
              ) : (
                <></>
              )}
              {values?.plan?.planType !== 'USAGE' && (
                <Grid
                  size={{
                    xs: 12,
                    sm: 12,
                    md: 12,
                    lg: 12,
                    xl: 12,
                  }}
                >
                  <div
                    className={clsx(
                      classes.subHeadingContainer,
                      classes.companyDetailSubHead,
                      classes.marginTopTcv
                    )}
                    data-testid="gcp-tcv"
                  >
                    <Typography
                      className={classes.subHeading}
                      data-testid="tcv-subheading"
                    >
                      Total Contract Value
                    </Typography>
                    <LabraToolTip
                      tooltipPlacement={'right'}
                      label={'Total contract value that the buyer has to pay.'}
                    >
                      <InfoOutlined className={classes.infoIcon} />
                    </LabraToolTip>
                  </div>
                  <div>
                    <Typography
                      className={classes.tcvLowerContainer}
                      data-testid="tcv-estimate"
                    >
                      $
                      {getTcvEstimateDetail(
                        values.plan?.serviceLength as number,
                        values.plan?.flatFeePrice as number
                      )}
                    </Typography>
                  </div>
                </Grid>
              )}
            </Grid>
          </div>
          {shouldShowDiscount && <MetricDiscount />}
        </>
      )}
      {values.plan?.planId && values.plan?.paymentType && (
        <PlanFeatures product={product} planId={values.plan.planId} />
      )}
    </Box>
  )
}
